import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast, { Toaster } from "react-hot-toast";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { FiUser, FiMail, FiLock, FiEye, FiEyeOff, FiHome, FiCheckCircle, FiShield } from 'react-icons/fi';
import { FaArrowRight, FaWhatsapp } from 'react-icons/fa';
import api from '../services/api';

// ─── SHARED INPUT WRAPPER ─────────────────────────────────────────────────────
const InputRow = ({ children }) => (
  <div className="reg-row flex items-center h-[66px] rounded-[22px] border border-[#EFE3D2] bg-white px-6 transition-all duration-300 focus-within:border-[#F29A0C] focus-within:shadow-[0_10px_40px_rgba(242,154,12,0.08)]">
    {children}
  </div>
);

const inputCls = "w-full h-full bg-transparent outline-none border-none px-4 text-[#111111] text-[15px] font-medium";

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const Register = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({ 
    userName: '', 
    fullName: '', 
    email: '', 
    phone: '', 
    password: '', 
    confirmPassword: '', 
    phoneOtp: '',
    emailOtp: '' 
  });

  const [showPassword, setShowPassword]               = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading]                         = useState(false);

  // Unified WhatsApp & Email OTP State
  const [otpSent, setOtpSent]                         = useState(false);
  const [otpVerified, setOtpVerified]                 = useState(false);
  const [sendingOtp, setSendingOtp]                   = useState(false);
  const [verifyingOtp, setVerifyingOtp]               = useState(false);
  const [otpTimer, setOtpTimer]                       = useState(0);
  const [verificationCode, setVerificationCode]       = useState('');

  const [error, setError]                     = useState('');
  const [agreed, setAgreed]                   = useState(false);

  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false, upperCase: false, lowerCase: false, number: false, specialChar: false,
  });

  // Countdown timer for WhatsApp & Email OTP Resend
  useEffect(() => {
    let interval = null;
    if (otpTimer > 0) {
      interval = setInterval(() => setOtpTimer(t => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  const hasEnteredContactInfo = Boolean(
    form.phone && form.phone.trim().length >= 6 &&
    form.email && form.email.trim().length >= 3 && form.email.includes('@')
  );

  const canSendPhoneOtp = form.userName && form.fullName && form.email && form.phone && form.password && form.confirmPassword;

  const isPasswordValid = () =>
    Object.values(passwordValidation).every(Boolean);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    if (name === "password") {
      setPasswordValidation({
        minLength:    value.length >= 6,
        upperCase:    /[A-Z]/.test(value),
        lowerCase:    /[a-z]/.test(value),
        number:       /\d/.test(value),
        specialChar:  /[^A-Za-z0-9]/.test(value),
      });
    }
    setError("");
  };

  // ─── UNIFIED WHATSAPP & EMAIL OTP DISPATCH ──────────────────────────────────
  const handleSendOtp = async () => {
    if (!agreed) { toast.error("Please agree to the Terms & Conditions and Privacy Policy first."); return; }
    if (!canSendPhoneOtp) { toast.error("Please fill all required registration fields first."); return; }
    if (!isPasswordValid()) { toast.error("Password must contain uppercase, lowercase, number, special character and minimum 6 characters."); return; }
    if (form.password !== form.confirmPassword) { toast.error("Passwords do not match"); return; }
    if (otpTimer > 0) { toast.error(`Please wait ${otpTimer} seconds before resending.`); return; }

    try {
      setSendingOtp(true); 
      setError('');
      const res = await api.post('/auth/send-registration-otp', { 
        email: form.email.trim(),
        phone: form.phone.trim()
      });
      if (res.data.success) {
        setOtpSent(true);
        setOtpTimer(30);
        toast.success(res.data.message || 'Verification code sent to your WhatsApp and Email address.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send verification code');
    } finally { 
      setSendingOtp(false); 
    }
  };

  const handleVerifyOtp = async () => {
    const code = (verificationCode || form.phoneOtp || form.emailOtp || '').trim();
    if (!code || code.length < 4) { 
      toast.error('Please enter the 6-digit verification code received on WhatsApp or Email'); 
      return; 
    }
    try {
      setVerifyingOtp(true);
      const res = await api.post('/auth/verify-registration-otp', { 
        email: form.email.trim(),
        phone: form.phone.trim(),
        otp: code 
      });
      if (res.data.success) { 
        setOtpVerified(true); 
        toast.success('✓ Verified successfully via WhatsApp / Email!'); 
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid or expired verification code');
    } finally { 
      setVerifyingOtp(false); 
    }
  };

  // ─── ACCOUNT CREATION SUBMISSION ────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) { toast.error("Please agree to the Terms & Conditions and Privacy Policy first."); return; }
    if (!canSendPhoneOtp) { toast.error('Please fill all fields'); return; }
    if (form.password !== form.confirmPassword) { toast.error("Passwords do not match"); return; }
    
    // Primary Requirement: WhatsApp / Email OTP verified
    if (!otpVerified) { 
      toast.error('Please verify your WhatsApp or Email verification code first.'); 
      return; 
    }
    
    if (!isPasswordValid()) { toast.error("Password does not meet security requirements."); return; }
    
    try {
      setLoading(true);
      const { confirmPassword, phoneOtp, emailOtp, ...payload } = form;
      payload.otp = verificationCode || "VERIFIED";
      
      await api.post('/auth/user/register', payload);
      toast.success("✓ Registration successful! Redirecting to login...");
      const redirectParam = new URLSearchParams(window.location.search).get('redirect') || sessionStorage.getItem('auth_redirect');
      setTimeout(() => navigate(redirectParam ? `/login?redirect=${encodeURIComponent(redirectParam)}` : "/login"), 1500);
    } catch (err) {
      const validationErrors = err.response?.data?.errors;
      if (validationErrors) setError(Object.values(validationErrors).flat()[0]);
      else toast.error(err.response?.data?.message || "Registration failed");
    } finally { 
      setLoading(false); 
    }
  };

  const isRegistrationReady = otpVerified;

  return (
    <div className="reg-wrap relative min-h-screen overflow-hidden bg-[#F8F5EF] flex items-center justify-center px-4 sm:px-6 py-10">
      <Toaster position="top-center" toastOptions={{ duration:5000, style:{ borderRadius:"16px", background:"#ffffff", color:"#111111", border:"1px solid #F29A0C", padding:"16px" } }}/>

      {/* Background */}
      <div className="absolute inset-0">
        <img src="/assets/image 25.jpeg" alt="" className="w-full h-full object-cover opacity-[0.82] scale-105"/>
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFF8EE]/92 via-[#FFF3E0]/88 to-[#FFE8C7]/88"/>
        <div className="absolute top-[-180px] right-[-120px] w-[420px] h-[420px] rounded-full bg-[#F29A0C]/16 blur-[120px]"/>
      </div>

      {/* Main card */}
      <motion.div initial={{ opacity:0, y:40 }} animate={{ opacity:1, y:0 }} transition={{ duration:1, ease:[0.22,1,0.36,1] }}
        className="reg-card relative z-20 w-full max-w-[1180px] overflow-hidden rounded-[42px] border border-white/40 bg-white/55 backdrop-blur-[30px] shadow-[0_40px_140px_rgba(242,154,12,0.10)] grid lg:grid-cols-2">

        {/* ── LEFT PANEL ── */}
        <div className="relative hidden lg:flex overflow-hidden min-h-[760px]">
          <img src="/assets/image 52.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/75 to-black/40"/>
          <div className="relative z-20 flex flex-col justify-between p-14 w-full">
            <div>
              <div className="inline-flex items-center gap-3 rounded-full border border-[#F29A0C]/20 bg-[#F29A0C]/10 backdrop-blur-xl px-5 py-3">
                <div className="w-2 h-2 rounded-full bg-[#F29A0C]"/>
                <span className="text-white text-[10px] tracking-[4px] uppercase font-semibold">DIVYYOG FOUNDATION</span>
              </div>
            </div>
            <div>
              <h1 className="text-[#F29A0C] text-[76px] leading-[1.2] tracking-[5px] font-semibold" style={{ fontFamily:"Poppins" }}>
                Begin<br/>Your<br/>Journey
              </h1>
              <p className="text-white text-[17px] leading-[34px] max-w-[430px] mt-8">
                A premium wellness experience designed to transform your lifestyle through yoga, mindfulness, and inner balance with Divyyog Foundation.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              {["Mindfulness","Meditation","Healing","Balance"].map((item, i) => (
                <motion.div key={i} whileHover={{ y:-3 }}
                  className="rounded-full border border-white/20 bg-white/10 backdrop-blur-xl px-5 py-3 text-white text-sm">
                  {item}
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="reg-panel relative flex items-center justify-center px-6 py-10 sm:px-12 md:px-14">
          <div className="absolute top-[-120px] right-[-100px] w-[240px] h-[240px] rounded-full bg-[#F29A0C]/12 blur-[90px]"/>

          <div className="relative z-20 w-full max-w-[460px]">

            {/* Logo */}
            <div className="flex justify-center lg:mb-6">
              <div className="w-[74px] h-[74px] rounded-[24px] overflow-hidden border border-[#F4D9B5] bg-white shadow-[0_20px_60px_rgba(0,0,0,0.05)]">
                <img src="/assets/logo.jpeg" alt="Divyyog Foundation" className="w-full h-full object-cover"/>
              </div>
            </div>

            {/* Header */}
            <div className="mt-6 lg:mt-0">
              <div className="reg-head flex items-center justify-between">
                <div className="reg-badge inline-flex items-center gap-3 rounded-full border border-[#F29A0C]/20 bg-[#F29A0C]/10 backdrop-blur-xl px-5 py-2.5">
                  <div className="w-2 h-2 rounded-full bg-[#F29A0C]"/>
                  <span className="text-[#F29A0C] text-[10px] tracking-[4px] uppercase font-semibold">DIVYYOG SIGN UP</span>
                </div>
                <button type="button" onClick={() => navigate("/")}
                  className="text-[#F29A0C] font-semibold text-sm flex items-center gap-1.5 hover:underline cursor-pointer">
                  <FiHome size={15}/> Home
                </button>
              </div>
              <h2 className="reg-title text-[34px] sm:text-[38px] leading-[1.05] tracking-[1px] font-semibold text-[#111111] mt-4" style={{ fontFamily:"Poppins" }}>
                Join The Experience
              </h2>
              <p className="text-[#666666] text-[14px] leading-[26px] mt-2">
                Start your holistic wellness journey with verified mobile access.
              </p>
            </div>

            {/* Messages */}
            {error && (
              <motion.div initial={{ opacity:0, y:-10 }} animate={{ opacity:1, y:0 }}
                className="mt-6 rounded-[20px] border border-[#FFD4D4] bg-[#FFF1F1] px-5 py-3 text-[#D92D20] text-sm font-semibold">
                {error}
              </motion.div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4 mt-6">

              {/* Username */}
              <InputRow>
                <FiUser className="text-[#F29A0C] text-lg shrink-0"/>
                <input name="userName" placeholder="Username" value={form.userName} onChange={handleChange} className={inputCls}/>
              </InputRow>

              {/* Full name */}
              <InputRow>
                <FiUser className="text-[#F29A0C] text-lg shrink-0"/>
                <input name="fullName" placeholder="Full Name" value={form.fullName} onChange={handleChange} className={inputCls}/>
              </InputRow>

              {/* Email */}
              <InputRow>
                <FiMail className="text-[#F29A0C] text-lg shrink-0"/>
                <input name="email" type="email" placeholder="Email Address" value={form.email} onChange={handleChange} className={inputCls}/>
              </InputRow>

              {/* Phone (Primary Identity) */}
              <div>
                <PhoneInput 
                  country={"in"} 
                  value={form.phone}
                  onChange={phone => setForm(p => ({ ...p, phone }))}
                  containerClass="divyyog-phone-container" 
                  buttonClass="divyyog-phone-button" 
                  inputClass="divyyog-phone-input"
                />
              </div>

              {/* Password */}
              <InputRow>
                <FiLock className="text-[#F29A0C] text-lg shrink-0"/>
                <input type={showPassword ? 'text' : 'password'} name="password" placeholder="Password" value={form.password} onChange={handleChange} className={inputCls}/>
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-[#999999] hover:text-[#111] cursor-pointer">
                  {showPassword ? <FiEyeOff size={18}/> : <FiEye size={18}/>}
                </button>
              </InputRow>

              {/* Password strength */}
              {form.password && (
                <div className="mt-2 text-xs space-y-1 bg-white/70 p-3 rounded-2xl border border-[#EFE3D2]">
                  {[
                    [passwordValidation.minLength,   "At least 6 characters"],
                    [passwordValidation.upperCase,   "One uppercase letter"],
                    [passwordValidation.lowerCase,   "One lowercase letter"],
                    [passwordValidation.number,      "One number"],
                    [passwordValidation.specialChar, "One special character"],
                  ].map(([valid, label]) => (
                    <p key={label} className={valid ? "text-emerald-600 font-bold" : "text-red-500 font-medium"}>
                      {valid ? "✓" : "✗"} {label}
                    </p>
                  ))}
                </div>
              )}

              {/* Confirm password */}
              <InputRow>
                <FiLock className="text-[#F29A0C] text-lg shrink-0"/>
                <input type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword" placeholder="Confirm Password" value={form.confirmPassword} onChange={handleChange} className={inputCls}/>
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="text-[#999999] hover:text-[#111] cursor-pointer">
                  {showConfirmPassword ? <FiEyeOff size={18}/> : <FiEye size={18}/>}
                </button>
              </InputRow>

              {/* Checkbox for Terms & Privacy */}
              <div className="flex items-start gap-3 pt-1 select-none">
                <input
                  type="checkbox"
                  id="termsAgree"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-1 w-4.5 h-4.5 rounded border-[#EFE3D2] accent-[#F29A0C] cursor-pointer"
                />
                <label htmlFor="termsAgree" className="text-[13px] text-[#555555] leading-relaxed cursor-pointer">
                  I agree to the <Link to="/terms-conditions" className="text-[#F29A0C] font-semibold hover:underline">Terms & Conditions</Link> and <Link to="/privacy-policy" className="text-[#F29A0C] font-semibold hover:underline">Privacy Policy</Link>
                </label>
              </div>

              {/* ─── DYNAMIC PROMINENT OTP VERIFICATION (REVEALED ONCE PHONE & EMAIL ARE ENTERED) ─── */}
              <AnimatePresence>
                {hasEnteredContactInfo && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0, y: 15 }} 
                    animate={{ opacity: 1, height: 'auto', y: 0 }} 
                    exit={{ opacity: 0, height: 0, y: 15 }} 
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    className="pt-2 overflow-hidden"
                  >
                    <div className="reg-verify-head flex items-center justify-between mb-2.5">
                      <span className="text-[12px] font-black uppercase text-[#111111] tracking-wider flex items-center gap-1.5">
                        <FiShield className="text-[#F29A0C]" size={15} /> 
                        Verification Step
                      </span>
                      <span className="text-[11px] font-bold text-[#16A34A] bg-[#DCFCE7] px-3 py-1 rounded-full border border-[#BBF7D0] flex items-center gap-1.5">
                        <FaWhatsapp className="text-[#25D366]" size={13} />
                        <FiMail className="text-[#16A34A]" size={12} />
                        WhatsApp & Email
                      </span>
                    </div>

                    {/* Explanatory Delivery Card */}
                    <div className="p-3.5 rounded-[20px] bg-[#F8F5EE] border border-[#EADBCA] mb-3.5 space-y-2">
                      <p className="text-[12px] text-[#444444] font-medium leading-relaxed">
                        We will send the <b className="text-[#111111] font-bold">same 6-digit verification code</b> to your WhatsApp and Email simultaneously. You can enter the code received on either channel.
                      </p>
                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-semibold">
                          <FaWhatsapp className="text-[#25D366]" size={13} /> +{form.phone || 'Phone'}
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-semibold">
                          <FiMail className="text-[#F29A0C]" size={12} /> {form.email || 'Email'}
                        </span>
                      </div>
                    </div>

                    {/* ─── UNIFIED OTP ACTION PANEL ─── */}
                    <motion.div initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.2 }} className="space-y-2">
                      {!otpSent && !otpVerified && (
                        <button 
                          type="button" 
                          onClick={handleSendOtp} 
                          disabled={sendingOtp}
                          className="reg-cta w-full h-[58px] rounded-[20px] bg-gradient-to-r from-[#F29A0C] via-[#E97818] to-[#16A34A] text-white font-bold text-[15px] transition-all duration-300 shadow-md shadow-[#F29A0C]/25 hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2.5 cursor-pointer disabled:opacity-60"
                        >
                          <FaWhatsapp size={19} className="text-[#25D366]" />
                          <FiMail size={16} />
                          <span>{sendingOtp ? "Sending Verification Code..." : "Send Code to WhatsApp & Email"}</span>
                        </button>
                      )}

                      {otpSent && !otpVerified && (
                        <div className="space-y-3 p-4.5 rounded-[26px] bg-[#FFF8EE] border border-[#F4D9B5]">
                          <div className="flex items-center justify-between">
                            <span className="text-[12px] font-black uppercase text-[#D97706] tracking-wider flex items-center gap-1.5">
                              <FiShield size={14} /> Enter 6-Digit Code
                            </span>
                            {otpTimer > 0 ? (
                              <span className="text-[11px] font-bold text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded-full">
                                Resend in {otpTimer}s
                              </span>
                            ) : (
                              <button 
                                type="button" 
                                onClick={handleSendOtp} 
                                disabled={sendingOtp}
                                className="text-[11px] font-bold text-[#F29A0C] hover:underline bg-transparent border-none cursor-pointer"
                              >
                                Resend Code
                              </button>
                            )}
                          </div>

                          <InputRow>
                            <FiShield className="text-[#F29A0C] text-lg shrink-0"/>
                            <input 
                              name="otp" 
                              value={verificationCode} 
                              onChange={(e) => {
                                setVerificationCode(e.target.value);
                                setForm(p => ({ ...p, phoneOtp: e.target.value, emailOtp: e.target.value }));
                              }} 
                              placeholder="Enter 6-Digit Code (from WhatsApp or Email)" 
                              maxLength={6} 
                              className={inputCls}
                              autoFocus
                            />
                          </InputRow>

                          <button 
                            type="button" 
                            onClick={handleVerifyOtp} 
                            disabled={verifyingOtp}
                            className="w-full h-[52px] rounded-[18px] bg-[#6B8A5A] hover:bg-[#5C794C] text-white font-bold text-[14px] transition-all duration-200 cursor-pointer shadow-md shadow-[#6B8A5A]/30 flex items-center justify-center gap-2"
                          >
                            <FiCheckCircle size={16} />
                            <span>{verifyingOtp ? "Verifying Code..." : "Verify Code"}</span>
                          </button>
                        </div>
                      )}

                      {otpVerified && (
                        <div className="rounded-[20px] border border-emerald-300 bg-emerald-50/90 px-5 py-3.5 text-emerald-800 text-center font-bold text-sm flex items-center justify-center gap-2 shadow-sm">
                          <FiCheckCircle className="text-emerald-600 text-lg shrink-0" />
                          <span>✓ Verified successfully via WhatsApp & Email!</span>
                        </div>
                      )}
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Submit Button */}
              {isRegistrationReady && (
                <motion.button 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ y:-2 }} 
                  whileTap={{ scale:0.98 }} 
                  disabled={loading}
                  type="submit"
                  className="reg-cta group relative overflow-hidden w-full h-[68px] rounded-[24px] bg-gradient-to-r from-[#F29A0C] via-[#E97818] to-[#FF8400] text-white font-bold text-[16px] shadow-[0_20px_60px_rgba(242,154,12,0.28)] cursor-pointer mt-3"
                >
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-all duration-1000 bg-[linear-gradient(120deg,transparent,rgba(255,255,255,0.25),transparent)]"/>
                  <span className="relative z-20 flex items-center justify-center gap-3">
                    {loading ? "Creating Divyyog Account..." : "Create Account"}
                    {!loading && <FaArrowRight/>}
                  </span>
                </motion.button>
              )}
            </form>

            {/* Footer */}
            <p className="text-center text-[#666666] text-[14px] mt-6">
              Already have an account?
              <Link to="/login" className="text-[#F29A0C] font-bold ml-1.5 hover:underline">Login</Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Register;